/**
 * The aggregate behind `2k` — Faturas & lançamentos.
 *
 * The screen reconciles ONE number the broker billed against TWO the platform
 * knows: the cost FinOps measured run through the contract's formula, and the
 * manual entries of the month. The difference between them is the whole point of
 * the screen, and it is why `Lançamentos manuais` shares the tab — a manual entry
 * exists precisely so the reconciliation closes.
 *
 * ⚠️ **The endpoint is assumed — §9.** `GET /faturas` does not exist yet.
 *
 * 🔴 **The approval flow is EM ABERTO (§9 item 5) and is deliberately NOT modelled
 * here.** The shot draws two states — `EM ABERTO` and `RECONCILIADA` — and no
 * approve/reject control. Inventing an `aprovar` action, an approver field or a
 * `em aprovação` state would be inventing iOX's process. `status` is therefore an
 * OPEN catalog: the day iOX defines the flow, the new states parse without a
 * frontend deploy, and only the actions have to be built.
 */
import { z } from 'zod'

import { competencia, dataIso, dinheiro, id, valorDeCatalogo } from './comum'

/**
 * 🔒 Open on purpose — see the file header. Known today: `em_aberto`,
 * `reconciliada`. Closing this into a `z.enum` would hard-fail the screen on the
 * first state iOX's approval flow introduces.
 */
export const statusDaFatura = valorDeCatalogo

export const faturaSchema = z.object({
  idFatura: id,
  idCliente: id,
  idContrato: id,
  /** `YYYY-MM` — the fiscal month, not the calendar month. */
  competencia,

  /**
   * The fiscal window this competência actually covers — `20/06–19/07` in the
   * shot. Sent by the server because the cycle start day lives on the contract
   * and an aditivo can move it; a screen that derived it from the competência
   * alone would silently drift after a repactuação (BR-21).
   */
  periodoInicio: dataIso,
  periodoFim: dataIso,

  status: statusDaFatura,

  /**
   * What the broker billed. `null` until someone registers it — and `null` is
   * why the screen can show `EM ABERTO` honestly instead of rendering `R$ 0,00`,
   * which would read as "the broker billed nothing".
   */
  valorFatura: dinheiro.nullable(),

  /** FinOps consumption converted by the contract's formula. Server-computed. */
  custoCalculado: dinheiro,
  /** Sum of the month's manual entries — the `Lançamentos manuais do mês` line. */
  totalLancamentosManuais: dinheiro,
  /**
   * `Diferença contra a fatura`. Server-sent rather than subtracted in the
   * screen: which entries count toward the comparison is a business rule, and
   * the screen must not be the place that decides it.
   *
   * `null` while `valorFatura` is `null` — there is nothing to differ from yet.
   */
  diferenca: dinheiro.nullable(),
})

export type Fatura = z.infer<typeof faturaSchema>
